/**
 * Workout Volume Parser
 *
 * Parses coach-written workout text and extracts running distance or
 * cross-training time (converted to mile-equivalent via easy pace).
 *
 * Supported running formats:
 *   4x200, 4 x 200, 4x200m         → 4 × 200m = 800m
 *   6x(300m / 100m)                 → 6 × (300+100)m = 2400m
 *   4xMile, 4x1Mile, 4x1.5Mile     → 4 × 1 mi, etc.
 *   4x3K, 4x3km                    → 4 × 3 km
 *   2 Mile WU, 1.5 miles           → standalone miles
 *   3K, 5km                        → standalone km
 *   800m, 400 meters                → standalone meters (50–10000)
 *
 * Supported timed interval formats (requires pace tags for distance calc):
 *   8x3'[INTERVAL] on 1'[EASY]     → work + rest distance via pace × time
 *   8x3' on 1' off                  → no tags = skipped (can't calc without pace)
 *
 * Supported cross-training formats (requires XT keyword + time):
 *   45 min bike, 90 minutes elliptical, 1.5 hour swim
 */

import { PACE_TAGS } from './paceTags';

const METERS_PER_MILE = 1609.344;

/** Strip *...* description blocks so they are not counted toward volume. */
function stripDescriptions(text) {
  return text.replace(/\*([^*]+)\*/g, ' ');
}

// tag inner key (e.g. 'INTERVAL') → zone key (e.g. 'interval')
const TAG_TO_ZONE = Object.fromEntries(
  PACE_TAGS.map(({ tag, zone }) => [tag.slice(1, -1), zone])
);

/** Parse a single distance string like "300m", "1km", "0.5mile", "300" → meters */
function parseDistanceMeters(str) {
  str = str.trim();
  let m = str.match(/^(\d+(?:\.\d+)?)\s*mi(?:les?)?$/i);
  if (m) return parseFloat(m[1]) * METERS_PER_MILE;
  m = str.match(/^(\d+(?:\.\d+)?)\s*k(?:m)?$/i);
  if (m) return parseFloat(m[1]) * 1000;
  m = str.match(/^(\d+(?:\.\d+)?)\s*m(?:eters?)?$/i) || str.match(/^(\d+(?:\.\d+)?)$/);
  if (m) {
    const n = parseFloat(m[1]);
    return (n >= 50 && n <= 10000) ? n : 0;
  }
  return 0;
}

/** Look up pace seconds/mile for a tag key like "INTERVAL" from paces object */
function getPaceSecFromTag(tagKey, paces) {
  if (!tagKey || !paces) return null;
  const zone = TAG_TO_ZONE[tagKey.toUpperCase()];
  if (!zone || !paces[zone]?.perMile) return null;
  return paceStringToSeconds(paces[zone].perMile);
}

const XT_KEYWORDS =
  /\b(bik(?:e|ing)|cycl(?:ing)|swim(?:ming)?|pool|elliptical|xt\b|cross[\s-]?train(?:ing)?|arc[\s-]?trainer|row(?:ing)?|aqua[\s-]?jog(?:ging)?)\b/i;

/**
 * Parse running volume from workout text.
 * Returns total miles (float).
 */
export function parseRunningMiles(text) {
  if (!text) return 0;

  // Strip *...* description blocks and pace tags like [EASY], [TEMPO]
  let t = stripDescriptions(text).replace(/\[[^\]]+\]/g, ' ');
  let totalMeters = 0;

  // GC (Gear Change): "1600m GC (400/800/400) ..." — count only the outer distance,
  // strip the inner parens to prevent sub-distances from being double-counted below.
  t = t.replace(
    /(\d+(?:\.\d+)?)\s*(m(?:eters?)?|km|k|miles?|mi)?\s*GC\s*\([^)]+\)/gi,
    (_, dist, unit) => {
      const d = parseFloat(dist);
      const u = (unit || '').toLowerCase();
      if (u.startsWith('mi') || u === 'mi') totalMeters += d * METERS_PER_MILE;
      else if (u === 'km' || u === 'k') totalMeters += d * 1000;
      else if (d >= 50 && d <= 100000) totalMeters += d;
      return ' ';
    }
  );

  // 0. Nested interval groups: "6x(300m / 100m)" → sum distances inside parens × count
  t = t.replace(
    /(\d+(?:\.\d+)?)\s*[xX×]\s*\(([^)]+)\)/g,
    (_, count, inner) => {
      const parts = inner.split(/[\/+,]/);
      const sumMeters = parts.reduce((s, p) => s + parseDistanceMeters(p), 0);
      totalMeters += parseFloat(count) * sumMeters;
      return ' ';
    }
  );

  // 1. Repeats with Mile: "4xMile", "4 x 1Mile", "4x1.5 Mile"
  t = t.replace(
    /(\d+(?:\.\d+)?)\s*[xX×]\s*(\d+(?:\.\d+)?)?\s*[Mm]iles?(?!\w)/g,
    (_, count, dist) => {
      totalMeters += parseFloat(count) * (dist ? parseFloat(dist) : 1) * METERS_PER_MILE;
      return ' ';
    }
  );

  // 2. Repeats with km: "4x3K", "4x3km", "2x5k"
  t = t.replace(
    /(\d+(?:\.\d+)?)\s*[xX×]\s*(\d+(?:\.\d+)?)\s*k(?:m)?(?!\w)/gi,
    (_, count, dist) => {
      totalMeters += parseFloat(count) * parseFloat(dist) * 1000;
      return ' ';
    }
  );

  // 3. Repeats with meters: "4x200", "4x200m", "3x300", "8x800", "12x200m"
  // Note: (?!['\u2019"\u201d]) prevents matching "8x3'" or "4x30"" (timed interval markers)
  t = t.replace(
    /(\d+(?:\.\d+)?)\s*[xX×]\s*(\d+(?:\.\d+)?)\s*m?(?![\w'\u2019"\u201d])/gi,
    (_, count, dist) => {
      totalMeters += parseFloat(count) * parseFloat(dist);
      return ' ';
    }
  );

  // 4. Standalone miles: "2 Mile WU", "1.5 miles", "3mi"
  t = t.replace(/(\d+(?:\.\d+)?)\s*[Mm]iles?(?!\w)/g, (_, d) => {
    totalMeters += parseFloat(d) * METERS_PER_MILE;
    return ' ';
  });
  t = t.replace(/(\d+(?:\.\d+)?)\s*mi(?!le|\w)/gi, (_, d) => {
    totalMeters += parseFloat(d) * METERS_PER_MILE;
    return ' ';
  });

  // 5. Standalone km: "3K", "5km"
  t = t.replace(/(\d+(?:\.\d+)?)\s*k(?:m)?(?!\w)/gi, (_, d) => {
    totalMeters += parseFloat(d) * 1000;
    return ' ';
  });

  // 6. Standalone meters: "800m", "400 meters" (50–10000 range only)
  t = t.replace(/(\d+)\s*m(?:eters?)?(?!\w)/gi, (_, d) => {
    const n = parseInt(d, 10);
    if (n >= 50 && n <= 10000) totalMeters += n;
    return ' ';
  });

  return totalMeters / METERS_PER_MILE;
}

/**
 * Parse cross-training volume from workout text.
 * easyPaceSecondsPerMile: athlete's easy pace in sec/mi (e.g. 455 for 7:35)
 * Returns equivalent miles.
 */

/** Internal: sum point-value XT time (hours/minutes/colons) from text, returns seconds. */
function _xtPointSeconds(text) {
  let s = 0;
  for (const [, n] of text.matchAll(/(\d+(?:\.\d+)?)\s*h(?:ours?|r)?s?(?!\w)/gi))
    s += parseFloat(n) * 3600;
  for (const [, n] of text.matchAll(/(?<![xX\u00d7\d])(\d+(?:\.\d+)?)\s*['\u2019]/g))
    s += parseFloat(n) * 60;
  for (const [, n] of text.matchAll(/(\d+(?:\.\d+)?)\s*min(?:utes?)?(?!\w)/gi))
    s += parseFloat(n) * 60;
  for (const [, h, m] of text.matchAll(/\b(\d{1,2}):([0-5]\d)\b/g)) {
    const hh = parseInt(h, 10), mm = parseInt(m, 10);
    s += hh <= 4 ? hh * 3600 + mm * 60 : hh * 60 + mm;
  }
  return s;
}

/**
 * Returns { min, max } miles for XT duration ranges in the text (e.g. "40'-45' bike").
 * Accumulates ALL ranges found (supports multiple sessions like AM + PM).
 * Point-value XT (e.g. "45 min swim") adds equally to both min and max.
 * Returns null if no range notation is detected anywhere in the text.
 */
export function parseCrossTrainingMilesRange(text, easyPaceSecondsPerMile) {
  if (!text || !easyPaceSecondsPerMile || !XT_KEYWORDS.test(text)) return null;
  text = stripDescriptions(text);

  let minSec = 0;
  let maxSec = 0;
  let hasRange = false;
  let remaining = text;

  // Find all "40'-45'" ranges
  for (const m of text.matchAll(/(?<![xX\u00d7\d])(\d+(?:\.\d+)?)\s*['\u2019]\s*[-\u2013]\s*(\d+(?:\.\d+)?)\s*['\u2019]/g)) {
    minSec += parseFloat(m[1]) * 60;
    maxSec += parseFloat(m[2]) * 60;
    hasRange = true;
    remaining = remaining.replace(m[0], ' ');
  }

  // Find all "40-45 min" ranges in what's left
  for (const m of [...remaining.matchAll(/(\d+(?:\.\d+)?)\s*[-\u2013]\s*(\d+(?:\.\d+)?)\s*min(?:utes?)?(?!\w)/gi)]) {
    minSec += parseFloat(m[1]) * 60;
    maxSec += parseFloat(m[2]) * 60;
    hasRange = true;
    remaining = remaining.replace(m[0], ' ');
  }

  if (!hasRange) return null;

  // Any remaining point-value XT adds equally to both ends of the range
  if (XT_KEYWORDS.test(remaining)) {
    const extra = _xtPointSeconds(remaining);
    minSec += extra;
    maxSec += extra;
  }

  return {
    min: minSec / easyPaceSecondsPerMile,
    max: maxSec / easyPaceSecondsPerMile,
  };
}

export function parseCrossTrainingMiles(text, easyPaceSecondsPerMile) {
  if (!text || !easyPaceSecondsPerMile || !XT_KEYWORDS.test(text)) return 0;
  text = stripDescriptions(text);

  // Strip all range patterns first (use midpoint for each), to prevent double-counting
  // individual numbers that make up ranges.
  let remaining = text;
  let totalSeconds = 0;

  for (const m of text.matchAll(/(?<![xX\u00d7\d])(\d+(?:\.\d+)?)\s*['\u2019]\s*[-\u2013]\s*(\d+(?:\.\d+)?)\s*['\u2019]/g)) {
    totalSeconds += (parseFloat(m[1]) + parseFloat(m[2])) / 2 * 60;
    remaining = remaining.replace(m[0], ' ');
  }
  for (const m of [...remaining.matchAll(/(\d+(?:\.\d+)?)\s*[-\u2013]\s*(\d+(?:\.\d+)?)\s*min(?:utes?)?(?!\w)/gi)]) {
    totalSeconds += (parseFloat(m[1]) + parseFloat(m[2])) / 2 * 60;
    remaining = remaining.replace(m[0], ' ');
  }

  // Add point-value XT time from what remains
  if (XT_KEYWORDS.test(remaining)) {
    totalSeconds += _xtPointSeconds(remaining);
  }

  return totalSeconds > 0 ? totalSeconds / easyPaceSecondsPerMile : 0;
}

/**
 * Parse timed interval volume from pace-tagged text.
 * Format: "8x3'[INTERVAL] on 1'[EASY] off"
 * Both work and rest pace tags are optional — segments without a tag are skipped.
 * paces: output of calculatePaces()
 */
export function parseTimedIntervalMiles(text, paces) {
  if (!text || !paces) return 0;
  text = stripDescriptions(text);
  let totalMiles = 0;
  let remaining = text;

  // Unit alternatives: quote chars or words (min/minutes, sec/seconds, s)
  const UNIT = `['"'\u2019"\u201d]|min(?:utes?)?\\b|sec(?:onds?)?\\b|s\\b`;
  function toSec(num, unit) {
    const u = (unit || '').trim().toLowerCase();
    const n = parseFloat(num);
    return (u === '"' || u === '\u201d' || u === 's' || u.startsWith('sec')) ? n : n * 60;
  }

  // Format A: tag after duration — "8x3'[LT1] on 1'[EASY] off"
  const fA = new RegExp(
    `(\\d+(?:\\.\\d+)?)\\s*[xX×]\\s*(\\d+(?:\\.\\d+)?)\\s*(${UNIT})\\s*(?:\\[([A-Z][A-Z0-9]*)\\])?\\s*on\\s*(\\d+(?:\\.\\d+)?)\\s*(${UNIT})\\s*(?:\\[([A-Z][A-Z0-9]*)\\])?\\s*(?:off)?`,
    'gi'
  );
  for (const [match, count, wDur, wUnit, wTag, rDur, rUnit, rTag] of text.matchAll(fA)) {
    const n = parseFloat(count);
    const wPace = getPaceSecFromTag(wTag, paces);
    const rPace = getPaceSecFromTag(rTag, paces);
    if (wPace) totalMiles += n * toSec(wDur, wUnit) / wPace;
    if (rPace) totalMiles += n * toSec(rDur, rUnit) / rPace;
    remaining = remaining.replace(match, ' ');
  }

  // Format B: tag after on/off keyword — "4x30" on [LT1] 30" off [RECOVERY]"
  const fB = new RegExp(
    `(\\d+(?:\\.\\d+)?)\\s*[xX×]\\s*(\\d+(?:\\.\\d+)?)\\s*(${UNIT})\\s*on\\s*\\[([A-Z][A-Z0-9]*)\\]\\s*(\\d+(?:\\.\\d+)?)\\s*(${UNIT})\\s*off\\s*\\[([A-Z][A-Z0-9]*)\\]`,
    'gi'
  );
  for (const [match, count, wDur, wUnit, wTag, rDur, rUnit, rTag] of remaining.matchAll(fB)) {
    const n = parseFloat(count);
    const wPace = getPaceSecFromTag(wTag, paces);
    const rPace = getPaceSecFromTag(rTag, paces);
    if (wPace) totalMiles += n * toSec(wDur, wUnit) / wPace;
    if (rPace) totalMiles += n * toSec(rDur, rUnit) / rPace;
    remaining = remaining.replace(match, ' ');
  }

  // Format C: NxDur<unit> [optional desc] [TAG] — e.g. "6x30" hills [APOWER]", "8x3'[LT1]"
  // Handles timed reps without on/off; negative lookahead prevents matching Format B patterns
  // Trailing capture ([^,;\n]*) detects "jog down" after the tag for hill rep doubling
  const fC = new RegExp(
    `(\\d+(?:\\.\\d+)?)\\s*[xX×]\\s*(\\d+(?:\\.\\d+)?)\\s*(${UNIT})\\s*(?!\\bon\\b|\\boff\\b)[^[\\n]*?\\s*\\[([A-Z][A-Z0-9]*)\\]([^,;\\n]*)`,
    'gi'
  );
  for (const [match, count, wDur, wUnit, wTag, trailing] of remaining.matchAll(fC)) {
    const n = parseFloat(count);
    const wPace = getPaceSecFromTag(wTag, paces);
    if (wPace) {
      const workMiles = n * toSec(wDur, wUnit) / wPace;
      const isJogDown = /jog\s+down/i.test(trailing);
      totalMiles += isJogDown ? workMiles * 2 : workMiles;
    }
    remaining = remaining.replace(match, ' ');
  }

  // Single timed segments — "1'[ANPOWER]", "10' @ [RECOVERY]" (ladder/pyramid style)
  const singlePattern = /(\d+(?:\.\d+)?)['\u2019]\s*(?:@\s*|at\s+)?\[([A-Z][A-Z0-9]*)\]/gi;
  for (const [, min, tagKey] of remaining.matchAll(singlePattern)) {
    const paceSec = getPaceSecFromTag(tagKey, paces);
    if (paceSec) totalMiles += (parseFloat(min) * 60) / paceSec;
  }

  return totalMiles;
}

/**
 * Total volume for a day (running + timed intervals + cross-training equivalent).
 * paces: optional output of calculatePaces() — needed for timed interval distance calc.
 */
export function getDayVolumeMiles(workoutText, easyPaceSecondsPerMile, paces) {
  return parseRunningMiles(workoutText) +
    parseTimedIntervalMiles(workoutText, paces) +
    parseCrossTrainingMiles(workoutText, easyPaceSecondsPerMile);
}

/**
 * Parse a "M:SS" pace string to seconds per mile. e.g. "7:35" → 455
 */
export function paceStringToSeconds(paceStr) {
  if (!paceStr) return null;
  const parts = paceStr.split(':').map(Number);
  if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
    return parts[0] * 60 + parts[1];
  }
  return null;
}

/**
 * Format miles for display.
 * < 0.1 mi → show meters ("150m")
 * >= 0.1 mi → show miles ("8.06 mi", "6 mi")
 */
export function formatVolume(miles) {
  if (!miles || miles < 0.01) return null;
  if (miles < 0.1) return `${Math.round(miles * METERS_PER_MILE)}m`;
  const rounded = Math.round(miles * 100) / 100;
  return rounded % 1 === 0 ? `${rounded} mi` : `${rounded.toFixed(2)} mi`;
}

/** Format a { min, max } miles range for display, e.g. "0.88-1.10 mi" */
export function formatVolumeRange(range) {
  if (!range) return null;
  return `${formatVolume(range.min)}-${formatVolume(range.max)}`;
}
